/**
 * The Direction strip — prototype H's top band: a ~30px image sliver per
 * Direction (rank + name + a health dot), expanding on hover to reveal the
 * why and the full health line.
 *
 * Weight rule (ticket 02): the strip is level 3 — imagery plus health state,
 * never spotlight-loud. Erosion is the confront verb, and it lives here and
 * nowhere else: a hold Direction past its `erosionDays` shows a red dot and a
 * dimmed tile. While Mode is on, confrontations freeze — "no new
 * confrontations surface while Mode is on" — so erosion is rendered flat.
 */

import type { BoardStateData, DefsDirection } from '../types';
import { sourceStatus } from '../freshness';
import { daysBetween, directionImage, esc } from './util';

export interface DirectionView {
  direction: DefsDirection;
  /** the health line: signal for build, "last invested Nd" for hold. */
  health: string;
  eroding: boolean;
  /** the health line's source is stale/failed — grey it, mark it. */
  stale: boolean;
  asOf: string | null;
}

export function directionViews(
  board: BoardStateData,
  opts: { suppressConfrontations: boolean; now?: Date },
): DirectionView[] {
  const now = opts.now ?? new Date();
  const state = board.state.data;
  const signals = Object.values(board.signals ?? {});

  return board.defs.data.directions.map((direction) => {
    if (direction.mode === 'hold') {
      const lastOn = state?.directions?.[direction.id]?.lastInvestedOn ?? null;
      const days = lastOn ? daysBetween(lastOn, board.today) : null;
      const threshold = direction.erosionDays ?? Infinity;
      // No investment ever recorded is not evidence of erosion (write-door
      // open question #6 — seed a baseline at go-live). Say so instead of
      // crying wolf.
      const eroding = !opts.suppressConfrontations && days !== null && days > threshold;
      const stateStatus = sourceStatus('state', board.state, now);
      return {
        direction,
        health: days === null ? 'no investment logged' : days === 0 ? 'invested today' : `last invested ${days}d`,
        eroding,
        stale: !stateStatus.healthy,
        asOf: stateStatus.healthy ? null : stateStatus.asOf,
      };
    }

    const attached = signals.find(
      (env) => env.data?.attachTo?.kind === 'direction' && env.data.attachTo.id === direction.id,
    );
    const status = attached ? sourceStatus('signal', attached, now) : null;
    return {
      direction,
      health: attached?.data?.label ?? '',
      eroding: false,
      stale: !!status && !status.healthy,
      asOf: status && !status.healthy ? status.asOf : null,
    };
  });
}

export function renderStrip(views: DirectionView[]): string {
  return `<div class="dir-strip">${views
    .map((v) => {
      const d = v.direction;
      const health = v.health
        ? `${esc(v.health)}${v.asOf ? ` <span class="asof">as of ${esc(v.asOf)}</span>` : ''}`
        : '';
      return `<div class="dir ${v.eroding ? 'eroding' : ''} ${v.stale ? 'stale' : ''}" style="background-image:${directionImage(d)}" title="${esc(d.heading)} — ${esc(d.why)}">
        <div class="veil"></div><span class="dot"></span>
        <span class="health">${health}</span>
        <span class="why2">${esc(d.why)}</span>
        <span class="lbl">#${d.rank} ${esc(d.heading)}</span>
      </div>`;
    })
    .join('')}</div>`;
}
